if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('form');
      this.form.querySelector('[name=id]').disabled = false;
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      this.isCartPage = window.location.pathname.includes('/cart');
      this.submitButton = this.querySelector('[type="submit"]');
      this.loadingSpinner = this.querySelector('.loading-overlay__spinner');
      this.prePurchaseUpsell = document.querySelector('ed-pre-purchase-upsell');
      this.errorMessageWrapper = this.querySelector('.product-form__error-message-wrapper');
      this.errorMessage = this.errorMessageWrapper?.querySelector('.product-form__error-message');

      this.isSubmitting = false;

      // Defer the aria-haspopup attribute setting
      this.initCartDrawerAccessibility();
    }

    // Lazy getter for cart - gets fresh reference each time
    getCart() {
      if (this.isCartPage) {
        return document.querySelector('cart-items');
      }
      return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    }

    // Lazy getter for cart drawer
    getCartDrawer() {
      return document.querySelector('cart-drawer');
    }

    // Wait for cart to be fully ready with all methods
    async waitForCart() {
      const maxWait = 10000;
      const startTime = Date.now();
      
      // First, wait for the element to exist in DOM
      while (!this.getCart() && (Date.now() - startTime) < maxWait) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
      
      const cart = this.getCart();
      if (!cart) return null;
      
      // Wait for custom element to be defined
      const tagName = cart.tagName.toLowerCase();
      if (customElements.get(tagName) === undefined) {
        try {
          await Promise.race([
            customElements.whenDefined(tagName),
            new Promise((_, reject) => setTimeout(() => reject('timeout'), maxWait - (Date.now() - startTime)))
          ]);
        } catch (e) {
          // Timeout, continue anyway
        }
      }
      
      // Wait for methods to be available
      while ((Date.now() - startTime) < maxWait) {
        const currentCart = this.getCart();
        if (currentCart && 
            typeof currentCart.getSectionsToRender === 'function' && 
            typeof currentCart.renderContents === 'function') {
          return currentCart;
        }
        await new Promise(resolve => setTimeout(resolve, 50));
      }
      
      return this.getCart();
    }

    initCartDrawerAccessibility() {
      requestAnimationFrame(() => {
        const cartDrawer = this.getCartDrawer();
        if (cartDrawer && this.submitButton) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }
      });
    }

    async onSubmitHandler(evt) {
      evt.preventDefault();

      if (this.isSubmitting || this.submitButton?.getAttribute('aria-disabled') === 'true') {
        return;
      }

      try {
        this.isSubmitting = true;

        const upsellResult = await this.handlePrePurchaseUpsell(evt);
        if (upsellResult === null) {
          return;
        }

        this.cleanupCountdownStorage();
        this.handleErrorMessage();
        this.setLoadingState(true);

        if (!this.isCartPage) {
          await this.waitForCart();
        }

        const formData = this.prepareFormData(upsellResult);
        const config = this.createFetchConfig(formData);
        
        // Add to cart
        const response = await this.addToCart(config);

        // Handle the response
        await this.handleCartResponse(response, formData);
        
      } catch (error) {
        console.error('Error during form submission:', error);
        this.handleErrorMessage(error.message || 'An unexpected error occurred');
      } finally {
        this.setLoadingState(false);
        this.isSubmitting = false;
      }
    }

    async handlePrePurchaseUpsell(evt) {
      if (!this.prePurchaseUpsell) {
        return undefined;
      }

      try {
        const upsellResult = await new Promise(resolve => {
          const event = new CustomEvent('ed:prepurchaseupsell:trigger', {
            detail: { resolve, evt },
            bubbles: false
          });
          document.dispatchEvent(event);
        });

        if (!upsellResult?.success) {
          return null;
        }

        return upsellResult;
      } catch (error) {
        console.error('Error handling pre-purchase upsell:', error);
        return undefined;
      }
    }

    cleanupCountdownStorage() {
      try {
        const hasCountdownSessionStorage = window.sessionStorage?.getItem('countdown');
        if (hasCountdownSessionStorage) {
          window.sessionStorage.removeItem('countdown');
        }
      } catch (error) {
        console.error('Error cleaning up session storage:', error);
      }
    }

    setLoadingState(isLoading) {
      if (!this.submitButton) return;
      const cart = this.getCart();

      if (isLoading) {
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.classList.add('loading');
        this.loadingSpinner?.classList.remove('hidden');
      } else {
        this.submitButton.classList.remove('loading');
        this.loadingSpinner?.classList.add('hidden');

        this.showSuccessState();

        if (cart?.classList.contains('is-empty')) {
          cart.classList.remove('is-empty');
        }

        if (!this.error) {
          this.submitButton.removeAttribute('aria-disabled');
        }
      }
    }

    showSuccessState() {
      if (!this.submitButton?.dataset.success) return;

      const buttonSpan = this.submitButton.querySelector('span');
      if (!buttonSpan) return;

      this.submitButton.classList.add('success');
      this.submitButton.dataset.text = buttonSpan.innerText.trim();
      buttonSpan.innerText = this.submitButton.dataset.success.trim();

      setTimeout(() => {
        buttonSpan.innerText = this.submitButton.dataset.text.trim();
        this.submitButton.classList.remove('success');
      }, 1500);
    }

    prepareFormData(upsellResult) {
      const formData = new FormData(this.form);
      const cart = this.getCart();

      if (cart && typeof cart.getSectionsToRender === 'function') {
        const sections = cart.getSectionsToRender().map((section) =>
          this.isCartPage ? section.section : section.id
        );
        formData.append('sections', sections);
        formData.append('sections_url', window.location.pathname);

        if (!this.isCartPage && typeof cart.setActiveElement === 'function') {
          cart.setActiveElement(document.activeElement);
        }
      }

      if (upsellResult) {
        const { variantId, quantity, discount } = upsellResult;
        if (variantId != null && quantity > 0 && discount > 0) {
          formData.append('items[1][id]', variantId);
          formData.append('items[1][quantity]', quantity);
          formData.append('items[1]properties[_ed_upsell]', discount);
        }
      }

      return formData;
    }

    createFetchConfig(formData) {
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      config.body = formData;
      return config;
    }

    async addToCart(config) {
      try {
        const response = await fetch(`${routes.cart_add_url}`, config);
        return await response.json();
      } catch (error) {
        console.error('Error adding to cart:', error);
        throw new Error('Failed to add item to cart');
      }
    }

    async handleCartResponse(response, formData) {
      // Check for Shopify error response
      if (response.status) {
        this.handleCartError(response, formData);
        return;
      }

      // Product was added successfully!
      this.error = false;
      this.handleDataLayer(formData);

      // Publish cart update event
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'product-form',
        productVariantId: formData.get('id'),
        cartData: response,
      });

      // Update cart quantity in header
      await this.updateCartQuantity();

      // If no sections in response, we can't update the drawer visually
      // But the product IS added, so just show success state (handled in finally block)
      if (!response.sections) {
        await this.manuallyRefreshCart(response);
        return;
      }

      const cart = this.getCart();

      if (!cart || typeof cart.renderContents !== 'function') {
        await this.manuallyRefreshCart(response);
        return;
      }

      // Handle different contexts
      const quickAddModal = this.closest('quick-add-modal');
      if (quickAddModal) {
        await this.handleQuickAddModal(quickAddModal, response, cart);
      } else if (!this.isCartPage) {
        await this.handleNonCartPage(response, cart);
      } else {
        await this.handleCartPage(response, cart);
      }
    }

    // Manually fetch cart sections and refresh the drawer (locale-safe: uses current path)
    async manuallyRefreshCart(addResponse) {
      try {
        const cart = this.getCart();
        if (!cart || typeof cart.getSectionsToRender !== 'function') {
          return;
        }

        const sections = cart.getSectionsToRender().map(s => s.id || s.section).join(',');
        const path = window.location.pathname;
        const firstSeg = path.split('/').filter(Boolean)[0];
        const localeParam = firstSeg && firstSeg.includes('-') ? `&_locale=${encodeURIComponent(firstSeg)}` : '';
        const response = await fetch(`${path}?sections=${sections}${localeParam}`);
        const sectionsData = await response.json();

        if (cart && typeof cart.renderContents === 'function' && sectionsData) {
          const state = { sections: sectionsData };
          if (addResponse && addResponse.id) state.id = addResponse.id;
          cart.renderContents(state);
        }
      } catch (error) {
        console.error('Error manually refreshing cart:', error);
      }
    }

    handleCartError(response, formData) {
      publish(PUB_SUB_EVENTS.cartError, {
        source: 'product-form',
        productVariantId: formData.get('id'),
        errors: response.errors || response.description,
        message: response.message,
      });

      this.handleErrorMessage(response.description);

      const soldOutMessage = this.submitButton?.querySelector('.sold-out-message');
      if (soldOutMessage) {
        this.submitButton.setAttribute('aria-disabled', 'true');
        const buttonSpan = this.submitButton.querySelector('span');
        if (buttonSpan) {
          buttonSpan.classList.add('hidden');
        }
        soldOutMessage.classList.remove('hidden');
      }

      this.error = true;
    }

    async handleQuickAddModal(quickAddModal, response, cart) {
      document.body.addEventListener('modalClosed', () => {
        setTimeout(() => {
          if (cart && typeof cart.renderContents === 'function') {
            cart.renderContents(response);
          }
        });
      }, { once: true });
      quickAddModal.hide(true);
    }

    async handleNonCartPage(response, cart) {
      if (!cart || typeof cart.renderContents !== 'function') {
        return; // Product added, just can't show drawer
      }

      // Always fetch drawer from current page URL so sections are in the correct locale
      // (cart/add.js response sections can be rendered in default locale by Shopify)
      await this.manuallyRefreshCart(response);
    }

    async handleCartPage(response, cart) {
      if (!cart) {
        window.location.reload();
        return;
      }

      if (cart.currentItemCount === 0) {
        setTimeout(() => {
          const cartItems = document.querySelector('cart-items');
          const mainContent = document.getElementById('MainContent');

          if (cartItems) cartItems.classList.add('is-empty');
          if (mainContent) mainContent.classList.add('cart-is-empty');
        }, 0);
        window.location.reload();
        return;
      }

      this.updateCartSections(response, cart);
    }

    updateCartSections(response, cart) {
      if (!cart || typeof cart.getSectionsToRender !== 'function') return;
      if (!response.sections) return;

      cart.getSectionsToRender().forEach((section) => {
        const sectionElement = document.getElementById(section.id);
        if (!sectionElement) return;

        const elementToReplace = sectionElement.querySelector(section.selector) || sectionElement;
        const newContent = cart.getSectionInnerHTML(
          response.sections?.[section.section],
          section.selector
        );

        if (newContent) {
          elementToReplace.innerHTML = newContent;
        }
      });
      
      try {
        const cartDrawerItems = cart.querySelector('cart-drawer-items');
        if (cartDrawerItems?.currentItemCount) {
          cartDrawerItems.currentItemCount -= 1;
        }
      } catch (error) {
        console.error('Error updating cart drawer item count:', error);
      }
    }

    async updateCartQuantity() {
      try {
        const response = await fetch(`${window.Shopify.routes.root}cart.js`);
        const cartData = await response.json();
        document.dispatchEvent(new CustomEvent('cart-quantity-updated', {
          bubbles: true,
          detail: cartData
        }));
      } catch (error) {
        console.error('Error updating cart quantity:', error);
      }
    }

    handleErrorMessage(errorMessage = false) {
      if (!this.errorMessageWrapper) return;

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage && this.errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }

    handleDataLayer(formData) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'add_to_cart',
        _clear: true,
        ecommerce: {
          items: [
            {
              item_id: `shopify_${window.Shopify.country}_${formData.get('product-id')}_${formData.get('id')}`,
              item_name: formData.get('title'),
              currency: window.Shopify.currency.active,
              item_brand: formData.get('vendor'),
              item_category: formData.get('product-type'),
              price: parseInt(formData.get('price')) / 100,
              quantity: parseInt(formData.get('quantity') || 1)
            }
          ]
        }
      });
    }
  });
}